import React, { useState, useEffect } from 'react';
import CartDrawer from './components/CartDrawer';
import FloatingButtons from './components/FloatingButtons';
import Home from './pages/Home';
import ContactPage from './pages/ContactPage';
import AuthPage from './pages/AuthPage';
import CategoryPage from './pages/CategoryPage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import ScrollToTopButton from './components/ScrollToTopButton';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com/rudraksh_backend';

export default function App() {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'category', 'details', 'contact', 'auth'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // States for products strictly from Django Backend Live API
  const [pujaList, setPujaList] = useState([]);
  const [herbalList, setHerbalList] = useState([]);
  const [jaivikList, setJaivikList] = useState([]);
  const [ayurvedicList, setAyurvedicList] = useState([]);
  const [organicList, setOrganicList] = useState([]);
  const [bestsellerProducts, setBestsellerProducts] = useState([]);

  // Django Backend se live APIs se data load karna
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/puja/`)
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data.results || []);
        setPujaList(items);
      })
      .catch(err => console.log('Error fetching puja data:', err));

    fetch(`${API_BASE_URL}/api/herbal/`)
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data.results || []);
        setHerbalList(items);
      })
      .catch(err => console.log('Error fetching herbal data:', err));

    fetch(`${API_BASE_URL}/api/jaivik/`)
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data.results || []);
        setJaivikList(items);
      })
      .catch(err => console.log('Error fetching jaivik data:', err));

    fetch(`${API_BASE_URL}/api/ayurvedic/`)
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data.results || []);
        setAyurvedicList(items);
      })
      .catch(err => console.log('Error fetching ayurvedic data:', err));

    fetch(`${API_BASE_URL}/api/organic/`)
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data.results || []);
        setOrganicList(items);
      })
      .catch(err => console.log('Error fetching organic data:', err));

    // Admin Bestsellers API
    fetch(`${API_BASE_URL}/api/bestsellers/`)
      .then(res => res.json())
      .then(data => {
        const items = Array.isArray(data) ? data : (data.results || []);
        setBestsellerProducts(items);
      })
      .catch(err => {
        console.error('Error fetching bestsellers:', err);
      });
  }, []);

  // Safe Master Navigation Functions
  const navigateToCategory = (catId, e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    const cleanCat = (catId && typeof catId === 'string') ? catId.trim().toLowerCase() : 'all';
    setActiveCategory(cleanCat);
    setCurrentPage('category');
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToHome = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveCategory('all');
    setCurrentPage('home');
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToPage = (pageName, e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    const targetPage = (typeof pageName === 'string') ? pageName : 'home';
    setCurrentPage(targetPage);
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cart Management Functions
  const addToCart = (product) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item.id === product.id);
      if (existing) {
        return prevCart.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  // Backend Order Placement Function
  const handlePlaceOrder = (customerDetails) => {
    const orderData = {
      customer_name: customerDetails.name,
      phone_number: customerDetails.phone,
      address: customerDetails.address,
      products_json: cart,
      total_amount: cart.reduce((acc, item) => acc + (item.price * item.quantity), 0)
    };

    fetch(`${API_BASE_URL}/api/orders/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    })
      .then(res => res.json())
      .then(data => {
        alert('Order successfully placed!');
        setCart([]);
        setIsCartOpen(false);
      })
      .catch(err => {
        console.error('Error placing order:', err);
        alert('Failed to place order. Please try again.');
      });
  };

  // View Details Function
  const handleViewDetails = (product, e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    if (product) {
      setSelectedProduct(product);
      setCurrentPage('details');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white relative">
      {currentPage === 'home' && (
        <Home
          activeCategory={activeCategory}
          setActiveCategory={navigateToCategory}
          adminProducts={bestsellerProducts}
          pujaProducts={pujaList}
          herbalProducts={herbalList}
          jaivikProducts={jaivikList}
          ayurvedicProducts={ayurvedicList}
          organicProducts={organicList}
          addToCart={addToCart}
          searchQuery={searchQuery}
          setCurrentPage={navigateToPage}
          setIsCartOpen={setIsCartOpen}
          cartCount={cartCount}
          setSearchQuery={setSearchQuery}
          onViewDetails={handleViewDetails}
        />
      )}

      {currentPage === 'category' && (
        <CategoryPage
          categoryId={activeCategory}
          pujaProducts={pujaList}
          herbalProducts={herbalList}
          jaivikProducts={jaivikList}
          ayurvedicProducts={ayurvedicList}
          organicProducts={organicList}
          adminProducts={bestsellerProducts}
          cartCount={cartCount}
          setIsCartOpen={setIsCartOpen}
          setActiveCategory={navigateToCategory}
          setCurrentPage={navigateToHome}
          addToCart={addToCart}
          onViewDetails={handleViewDetails}
          setSearchQuery={setSearchQuery}
          searchQuery={searchQuery}
        />
      )}

      {currentPage === 'details' && (
        <ProductDetailsPage
          product={selectedProduct}
          cartCount={cartCount}
          setIsCartOpen={setIsCartOpen}
          setActiveCategory={navigateToCategory}
          setCurrentPage={navigateToHome}
          addToCart={addToCart}
          setSearchQuery={setSearchQuery}
          adminProducts={bestsellerProducts}
          pujaProducts={pujaList}
          herbalProducts={herbalList}
          jaivikProducts={jaivikList}
          ayurvedicProducts={ayurvedicList}
          organicProducts={organicList}
          onViewDetails={handleViewDetails}
        />
      )}

      {currentPage === 'contact' && (
        <ContactPage
          cartCount={cartCount}
          setIsCartOpen={setIsCartOpen}
          setActiveCategory={navigateToCategory}
          setCurrentPage={navigateToHome}
          setSearchQuery={setSearchQuery}
          adminProducts={bestsellerProducts}
          pujaProducts={pujaList}
          herbalProducts={herbalList}
          jaivikProducts={jaivikList}
          ayurvedicProducts={ayurvedicList}
          organicProducts={organicList}
          onViewDetails={handleViewDetails}
        />
      )}

      {currentPage === 'auth' && (
        <AuthPage
          cartCount={cartCount}
          setIsCartOpen={setIsCartOpen}
          setActiveCategory={navigateToCategory}
          setCurrentPage={navigateToHome}
          setSearchQuery={setSearchQuery}
          adminProducts={bestsellerProducts}
          pujaProducts={pujaList}
          herbalProducts={herbalList}
          jaivikProducts={jaivikList}
          ayurvedicProducts={ayurvedicList}
          organicProducts={organicList}
          onViewDetails={handleViewDetails}
        />
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        setIsOpen={setIsCartOpen}
        cart={cart}
        setCart={setCart}
        onPlaceOrder={handlePlaceOrder}
      />

      {/* Scroll to Top Button */}
      <ScrollToTopButton />

      {/* Floating WhatsApp & Call Buttons */}
      <FloatingButtons />
    </div>
  );
}